const STORAGE_KEY = 'novelday:local-reading-progress';

const readStorage = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const writeStorage = (value) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
};

export const getLocalReadingProgress = (sessionId, novelId) => {
  if (!sessionId || !novelId || typeof window === 'undefined') {
    return null;
  }
  const storage = readStorage();
  const sessionProgress = storage[String(sessionId)] ?? {};
  const entry = sessionProgress[String(novelId)];
  if (!entry) {
    return null;
  }
  return {
    novelId: entry.novelId ?? Number(novelId),
    sessionId,
    chapterNumber: Number.isFinite(Number(entry.chapterNumber)) ? Number(entry.chapterNumber) : null,
    readChapters: Array.isArray(entry.readChapters) ? entry.readChapters : [],
    updatedAt: entry.updatedAt ?? null,
  };
};

export const setLocalReadingProgress = (sessionId, novelId, chapterNumber) => {
  if (!sessionId || !novelId || !Number.isFinite(Number(chapterNumber)) || typeof window === 'undefined') {
    return;
  }
  const storage = readStorage();
  const sessionKey = String(sessionId);
  const novelKey = String(novelId);
  const progressForSession = { ...(storage[sessionKey] ?? {}) };

  const existingEntry = progressForSession[novelKey] || {};

  // Position is forward-only for implicit writes — same rule as the
  // backend's updateProgress (chapterNumber >= existing). Re-opening an
  // earlier chapter must not move the Continue pointer backwards; explicit
  // corrections go through adjustLocalReadingProgress (SET_POSITION), which
  // may regress. This lets the explicit pin win everywhere a consumer reads
  // the position.
  const previousPosition = Number.isFinite(Number(existingEntry.chapterNumber))
    ? Number(existingEntry.chapterNumber)
    : null;
  const nextPosition = previousPosition != null
    ? Math.max(previousPosition, Number(chapterNumber))
    : Number(chapterNumber);

  // Only save the reading position (chapterNumber). Do NOT add the chapter
  // to readChapters — that must only happen when the reader spends a
  // proportional amount of time on the chapter (the time-gated threshold),
  // which is tracked by markLocalChapterRead (called from ChapterReader when
  // the backend confirms the view was recorded).
  // Preserve partialTimes so cross-visit accumulation for other chapters in
  // the same novel is not destroyed when the reading position is updated.
  progressForSession[novelKey] = {
    novelId: Number(novelId),
    chapterNumber: nextPosition,
    readChapters: existingEntry.readChapters || [],
    partialTimes: existingEntry.partialTimes,
    updatedAt: new Date().toISOString(),
  };
  storage[sessionKey] = progressForSession;
  writeStorage(storage);
};

/**
 * Marks a chapter as "read" in local storage for anonymous users. This is
 * the local-storage equivalent of the backend's time-gated read threshold:
 * it should only be called when the backend confirms the chapter view was
 * recorded (i.e., the reader spent enough time on the chapter).
 *
 * <p>For logged-in users, read status comes from the server
 * (ReadingProgressService.readChapters), so this function is not called.
 *
 * @param sessionId  the anonymous session ID
 * @param novelId    the novel ID
 * @param chapterNumber  the chapter number to mark as read
 */
export const markLocalChapterRead = (sessionId, novelId, chapterNumber) => {
  if (!sessionId || !novelId || !Number.isFinite(Number(chapterNumber)) || typeof window === 'undefined') {
    return;
  }
  const storage = readStorage();
  const sessionKey = String(sessionId);
  const novelKey = String(novelId);
  const progressForSession = { ...(storage[sessionKey] ?? {}) };

  const existingEntry = progressForSession[novelKey] || {};
  const existingRead = new Set(existingEntry.readChapters || []);
  existingRead.add(Number(chapterNumber));

  // Preserve partialTimes for OTHER chapters — previously this function wrote
  // a fresh entry without partialTimes, wiping every chapter's cross-visit
  // accumulator for the novel whenever ANY chapter was marked read (a reader
  // who was 40s into chapter 5 lost that progress the moment chapter 3 was
  // marked read). The marked chapter's own entry is dropped here too — it's
  // read, so its partial is moot (clearPartialReadingTime is also called by
  // callers, but doing it here is defensive).
  const updatedPartial = { ...(existingEntry[PARTIAL_TIMES_KEY] || {}) };
  delete updatedPartial[String(chapterNumber)];

  progressForSession[novelKey] = {
    novelId: Number(novelId),
    chapterNumber: existingEntry.chapterNumber ?? Number(chapterNumber),
    readChapters: Array.from(existingRead),
    partialTimes: updatedPartial,
    updatedAt: new Date().toISOString(),
  };
  storage[sessionKey] = progressForSession;
  writeStorage(storage);
};

/**
 * Manual position adjustment for anonymous readers — mirrors the backend's
 * PATCH /progress actions (SET_POSITION, RESET) against localStorage.
 * SET_POSITION moves the continue pointer exactly (the regular tracking path
 * is forward-only, so an accidental click on a later chapter is otherwise
 * permanent). readChapters is never touched — the ✓ marks stay owned by the
 * time-gated path.
 *
 * Returns the updated progress entry (same shape as getLocalReadingProgress),
 * or null for RESET.
 */
export const adjustLocalReadingProgress = (sessionId, novelId, action, chapterNumber) => {
  if (!sessionId || !novelId || !action || typeof window === 'undefined') {
    return null;
  }
  const num = Number(chapterNumber);
  const storage = readStorage();
  const sessionKey = String(sessionId);
  const novelKey = String(novelId);
  const existingEntry = storage[sessionKey]?.[novelKey];

  if (action === 'RESET') {
    clearLocalReadingProgress(sessionId, novelId);
    return null;
  }
  if (action !== 'SET_POSITION' || !Number.isFinite(num)) {
    return null;
  }

  const progressForSession = { ...(storage[sessionKey] ?? {}) };
  progressForSession[novelKey] = {
    novelId: Number(novelId),
    chapterNumber: num,
    readChapters: Array.isArray(existingEntry?.readChapters) ? existingEntry.readChapters : [],
    partialTimes: existingEntry?.partialTimes,
    updatedAt: new Date().toISOString(),
  };
  storage[sessionKey] = progressForSession;
  writeStorage(storage);

  return getLocalReadingProgress(sessionId, novelId);
};

export const clearLocalReadingProgress = (sessionId, novelId) => {
  if (!sessionId || !novelId || typeof window === 'undefined') {
    return;
  }
  const storage = readStorage();
  const sessionKey = String(sessionId);
  const novelKey = String(novelId);
  if (!storage[sessionKey]) {
    return;
  }
  delete storage[sessionKey][novelKey];
  if (Object.keys(storage[sessionKey]).length === 0) {
    delete storage[sessionKey];
  }
  writeStorage(storage);
};

export const getAllLocalReadingProgress = (sessionId) => {
  if (!sessionId || typeof window === 'undefined') {
    return [];
  }
  const storage = readStorage();
  const sessionProgress = storage[String(sessionId)] ?? {};
  
  return Object.values(sessionProgress)
    .map(entry => ({
      novelId: entry.novelId,
      chapterNumber: Number.isFinite(Number(entry.chapterNumber)) ? Number(entry.chapterNumber) : null,
      readChapters: Array.isArray(entry.readChapters) ? entry.readChapters : [],
      updatedAt: entry.updatedAt ?? null,
    }))
    .sort((a, b) => {
      const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return timeB - timeA;
    });
};

// ── Cross-visit partial reading time (anonymous only) ──────────────────
// Stores accumulated partial reading time per (sessionId, novelId, chapterNumber)
// so that short reading bursts across multiple visits can add up to meet the
// threshold. This is separate from readChapters — it's a transient accumulator
// that is cleared once the chapter is marked as read.
//
// Keyed by chapter number within the novel's progress entry:
//   { ..., partialTimes: { "1": 60, "2": 30 } }
//
// Uses REPLACE semantics: the component passes the TOTAL accumulated visible
// time (including partial time loaded from the previous visit), so the storage
// replaces the old value rather than adding to it. The value is capped at
// maxTimeCapMultiplier * estimatedSeconds to prevent unbounded growth.

const PARTIAL_TIMES_KEY = 'partialTimes';

/**
 * Returns the accumulated partial reading time (seconds) for a specific
 * chapter, or 0 if none stored.
 */
export const getPartialReadingTime = (sessionId, novelId, chapterNumber) => {
  if (!sessionId || !novelId || !Number.isFinite(Number(chapterNumber)) || typeof window === 'undefined') {
    return 0;
  }
  const storage = readStorage();
  const entry = storage[String(sessionId)]?.[String(novelId)];
  if (!entry || !entry[PARTIAL_TIMES_KEY]) {
    return 0;
  }
  const partial = entry[PARTIAL_TIMES_KEY][String(chapterNumber)];
  return Number.isFinite(Number(partial)) ? Number(partial) : 0;
};

/**
 * Stores (or adds to) the accumulated partial reading time for a specific
 * chapter. The value is capped at maxSeconds to prevent unbounded growth.
 */
export const setPartialReadingTime = (sessionId, novelId, chapterNumber, seconds, maxSeconds) => {
  if (!sessionId || !novelId || !Number.isFinite(Number(chapterNumber)) || typeof window === 'undefined') {
    return;
  }
  const clamped = Math.max(0, Math.floor(seconds));
  if (clamped <= 0) {
    return;
  }
  const cap = Number.isFinite(maxSeconds) && maxSeconds > 0 ? Math.floor(maxSeconds) : clamped;
  const storage = readStorage();
  const sessionKey = String(sessionId);
  const novelKey = String(novelId);
  const progressForSession = { ...(storage[sessionKey] ?? {}) };
  const existingEntry = progressForSession[novelKey] || {};
  const existingPartial = existingEntry[PARTIAL_TIMES_KEY] || {};
  // Replace (not add): the component passes the TOTAL accumulated visible
  // time (including partial time loaded from the previous visit), so the
  // storage should replace the old value, not add to it. Additive semantics
  // caused double-counting when both the periodic sync and unmount saved
  // in the same visit, or across visits.
  const updated = Math.min(clamped, cap);
  existingPartial[String(chapterNumber)] = updated;
  progressForSession[novelKey] = {
    ...existingEntry,
    novelId: Number(novelId),
    partialTimes: existingPartial,
    updatedAt: new Date().toISOString(),
  };
  storage[sessionKey] = progressForSession;
  writeStorage(storage);
};

/**
 * Clears the partial reading time for a specific chapter (called when the
 * chapter is marked as read — no need to accumulate further).
 */
export const clearPartialReadingTime = (sessionId, novelId, chapterNumber) => {
  if (!sessionId || !novelId || !Number.isFinite(Number(chapterNumber)) || typeof window === 'undefined') {
    return;
  }
  const storage = readStorage();
  const sessionKey = String(sessionId);
  const novelKey = String(novelId);
  if (!storage[sessionKey] || !storage[sessionKey][novelKey]) {
    return;
  }
  const progressForSession = { ...(storage[sessionKey] ?? {}) };
  const existingEntry = progressForSession[novelKey] || {};
  const existingPartial = existingEntry[PARTIAL_TIMES_KEY];
  if (!existingPartial) {
    return;
  }
  delete existingPartial[String(chapterNumber)];
  progressForSession[novelKey] = {
    ...existingEntry,
    partialTimes: existingPartial,
  };
  storage[sessionKey] = progressForSession;
  writeStorage(storage);
};
